import { el, on } from '../util/dom';
import { ScheduleWindow, DayLimitOverride, DAY_NAMES, timeToMinutes, minutesToTime } from './types';
import { SCHEDULES_EDITOR_STYLES } from './styles';

export interface ScheduleEditorProps {
  schedule?: ScheduleWindow;
  onSave: (schedule: Omit<ScheduleWindow, 'id'>) => Promise<void>;
  onCancel: () => void;
}

export function createScheduleEditor(props: ScheduleEditorProps): HTMLElement {
  const modal = el('div', { class: 'schedule-editor-modal' });

  // Header
  const header = el('div', { class: 'editor-header' });
  const title = el('h3', { class: 'editor-title' }, props.schedule ? 'Edit Curfew' : 'Add Curfew');
  header.appendChild(title);
  modal.appendChild(header);

  // Form
  const form = el('div', { class: 'editor-form' });

  // Days of week selection
  const daysSection = el('div', { class: 'editor-section' });
  const daysLabel = el('label', { class: 'editor-label' }, 'Days');
  daysSection.appendChild(daysLabel);

  const daysGrid = el('div', { class: 'days-grid' });
  let selectedDaysMask = props.schedule?.daysMask ?? 0;

  DAY_NAMES.forEach((dayName, dayIndex) => {
    const dayCheckbox = el('input', {
      type: 'checkbox',
      class: 'day-checkbox',
      'data-day': String(dayIndex),
    }) as HTMLInputElement;

    if (selectedDaysMask & (1 << dayIndex)) {
      dayCheckbox.checked = true;
    }

    on(dayCheckbox, 'change', (e) => {
      const target = e.target as HTMLInputElement;
      const dayBit = 1 << dayIndex;
      if (target.checked) {
        selectedDaysMask |= dayBit;
      } else {
        selectedDaysMask &= ~dayBit;
      }
    });

    const label = el('label', { class: 'day-label' });
    label.appendChild(dayCheckbox);
    label.appendChild(el('span', {}, dayName.substring(0, 3)));
    daysGrid.appendChild(label);
  });

  daysSection.appendChild(daysGrid);
  form.appendChild(daysSection);

  // Start time
  const startSection = el('div', { class: 'editor-section' });
  const startLabel = el('label', { class: 'editor-label' }, 'Start time');
  startSection.appendChild(startLabel);

  const startInput = el('input', {
    type: 'time',
    class: 'editor-input time-input',
    value: props.schedule ? minutesToTime(props.schedule.startMinute) : '22:00',
  }) as HTMLInputElement;
  startSection.appendChild(startInput);
  form.appendChild(startSection);

  // End time
  const endSection = el('div', { class: 'editor-section' });
  const endLabel = el('label', { class: 'editor-label' }, 'End time');
  endSection.appendChild(endLabel);

  const endInput = el('input', {
    type: 'time',
    class: 'editor-input time-input',
    value: props.schedule ? minutesToTime(props.schedule.endMinute) : '06:00',
  }) as HTMLInputElement;
  endSection.appendChild(endInput);
  form.appendChild(endSection);

  modal.appendChild(form);

  // Actions
  const actions = el('div', { class: 'editor-actions' });

  const cancelBtn = el('button', { class: 'btn btn-secondary' }, 'Cancel');
  on(cancelBtn, 'click', () => props.onCancel());
  actions.appendChild(cancelBtn);

  const saveBtn = el('button', { class: 'btn btn-primary' }, props.schedule ? 'Save' : 'Add') as HTMLButtonElement;
  on(saveBtn, 'click', async () => {
    // Validation
    if (selectedDaysMask === 0) {
      alert('Please select at least one day.');
      return;
    }

    const startTime = startInput.value;
    const endTime = endInput.value;

    if (!startTime || !endTime) {
      alert('Please select both start and end times.');
      return;
    }

    saveBtn.disabled = true;

    try {
      const startMinute = timeToMinutes(startTime);
      const endMinute = timeToMinutes(endTime);

      const newSchedule: Omit<ScheduleWindow, 'id'> = {
        daysMask: selectedDaysMask,
        startMinute,
        endMinute,
      };

      await props.onSave(newSchedule);
    } catch (err) {
      alert(`Failed to save curfew: ${(err as Error).message}`);
      saveBtn.disabled = false;
    }
  });
  actions.appendChild(saveBtn);

  modal.appendChild(actions);

  // Inject styles
  injectScheduleEditorStyles();

  return modal;
}

export interface DayOverrideEditorProps {
  override?: DayLimitOverride;
  onSave: (override: Omit<DayLimitOverride, 'id'>) => Promise<void>;
  onCancel: () => void;
}

export function createDayOverrideEditor(props: DayOverrideEditorProps): HTMLElement {
  const modal = el('div', { class: 'day-override-editor-modal' });

  // Header
  const header = el('div', { class: 'editor-header' });
  const title = el('h3', { class: 'editor-title' }, props.override ? 'Edit Daily Limit Override' : 'Add Daily Limit Override');
  header.appendChild(title);
  modal.appendChild(header);

  // Form
  const form = el('div', { class: 'editor-form' });

  // Day of week selection
  const daySection = el('div', { class: 'editor-section' });
  const dayLabel = el('label', { class: 'editor-label' }, 'Day');
  daySection.appendChild(dayLabel);

  const daySelect = el('select', {
    class: 'editor-input',
    value: String(props.override?.dayOfWeek ?? 0),
  }) as HTMLSelectElement;

  DAY_NAMES.forEach((dayName, dayIndex) => {
    const option = el('option', {
      value: String(dayIndex),
    }, dayName);
    daySelect.appendChild(option);
  });

  daySection.appendChild(daySelect);
  form.appendChild(daySection);

  // Limit minutes
  const limitSection = el('div', { class: 'editor-section' });
  const limitLabel = el('label', { class: 'editor-label' }, 'Daily Limit (minutes)');
  limitSection.appendChild(limitLabel);

  const limitInput = el('input', {
    type: 'number',
    class: 'editor-input',
    min: '0',
    step: '5',
    value: String(props.override?.limitMinutes ?? 120),
  }) as HTMLInputElement;
  limitSection.appendChild(limitInput);
  form.appendChild(limitSection);

  modal.appendChild(form);

  // Actions
  const actions = el('div', { class: 'editor-actions' });

  const cancelBtn = el('button', { class: 'btn btn-secondary' }, 'Cancel');
  on(cancelBtn, 'click', () => props.onCancel());
  actions.appendChild(cancelBtn);

  const saveBtn = el('button', { class: 'btn btn-primary' }, props.override ? 'Save' : 'Add') as HTMLButtonElement;
  on(saveBtn, 'click', async () => {
    // Validation
    const limitMinutes = parseInt(limitInput.value, 10);
    if (isNaN(limitMinutes) || limitMinutes < 0) {
      alert('Please enter a valid limit in minutes.');
      return;
    }

    saveBtn.disabled = true;

    try {
      const newOverride: Omit<DayLimitOverride, 'id'> = {
        dayOfWeek: parseInt(daySelect.value, 10),
        limitMinutes,
      };

      await props.onSave(newOverride);
    } catch (err) {
      alert(`Failed to save override: ${(err as Error).message}`);
      saveBtn.disabled = false;
    }
  });
  actions.appendChild(saveBtn);

  modal.appendChild(actions);

  // Inject styles
  injectDayOverrideEditorStyles();

  return modal;
}

function injectScheduleEditorStyles() {
  if (document.getElementById('schedule-editor-styles')) return;

  const style = el('style');
  style.id = 'schedule-editor-styles';
  style.textContent = SCHEDULES_EDITOR_STYLES;
  document.head.appendChild(style);
}

function injectDayOverrideEditorStyles() {
  if (document.getElementById('day-override-editor-styles')) return;

  const style = el('style');
  style.id = 'day-override-editor-styles';
  style.textContent = SCHEDULES_EDITOR_STYLES;
  document.head.appendChild(style);
}
